import { useState, useCallback, useMemo } from 'react';
import type { ColumnDef, PaginationState, SortingState } from '@tanstack/react-table';
import { History, Search, SquarePen, X } from 'lucide-react';
import { searchRaceData, createCorrection, getCorrectionHistory } from '../services/api';
import { getErrorMessage } from '../utils/errors';
import RaceDataEditDialog from '../components/race-data/RaceDataEditDialog';
import CorrectionHistoryDialog from '../components/race-data/CorrectionHistoryDialog';
import type { RaceLaneResult, RaceDataSearchParams, RaceDataCorrection } from '../types';
import { PageHeader, Alert, Badge, Button, DataTable, Field, Input, Tooltip } from '@racing/ui';

/** Empty, null and the literal "NA" all mean "no reading" in the timing feed. */
const dash = (value: unknown) =>
  value === null || value === undefined || value === '' || value === 'NA' ? '—' : String(value);

const RaceDataPage = () => {
  // Search form state
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [category, setCategory] = useState('');
  const [round, setRound] = useState('');
  const [carClass, setCarClass] = useState('');
  const [driverName, setDriverName] = useState('');
  const [carNo, setCarNo] = useState('');

  // Results state
  const [rows, setRows] = useState<RaceLaneResult[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'raceDate', desc: true }]);

  // Edit dialog
  const [editRecord, setEditRecord] = useState<RaceLaneResult | null>(null);
  const [originalRecord, setOriginalRecord] = useState<Record<string, any> | null>(null);

  // History dialog
  const [historyRecordId, setHistoryRecordId] = useState<number | null>(null);
  const [historyCorrections, setHistoryCorrections] = useState<RaceDataCorrection[]>([]);
  const [historyOriginal, setHistoryOriginal] = useState<Record<string, any> | null>(null);

  const buildParams = useCallback(
    (page: PaginationState, sort: SortingState): RaceDataSearchParams => {
      const params: RaceDataSearchParams = {
        page: page.pageIndex + 1, // API is 1-indexed; the table is 0-indexed.
        pageSize: page.pageSize,
      };
      if (fromDate) params.fromDate = `${fromDate} 00:00:00.000000`;
      if (toDate) params.toDate = `${toDate} 23:59:00.000000`;
      if (category) params.category = category;
      if (round) params.round = round;
      if (carClass) params.carClass = carClass;
      if (driverName) params.driverName = driverName;
      if (carNo) params.carNo = carNo;
      if (sort.length > 0) {
        params.sortField = sort[0].id;
        params.sortOrder = sort[0].desc ? 'desc' : 'asc';
      }
      return params;
    },
    [fromDate, toDate, category, round, carClass, driverName, carNo]
  );

  const fetchData = useCallback(
    async (page: PaginationState, sort: SortingState) => {
      try {
        setLoading(true);
        setError('');
        const result = await searchRaceData(buildParams(page, sort));
        setRows(result.data);
        setTotal(result.total);
        setHasSearched(true);
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  const handleSearch = () => {
    const next = { pageIndex: 0, pageSize: pagination.pageSize };
    setPagination(next);
    fetchData(next, sorting);
  };

  const handleClear = () => {
    setFromDate('');
    setToDate('');
    setCategory('');
    setRound('');
    setCarClass('');
    setDriverName('');
    setCarNo('');
    setRows([]);
    setTotal(0);
    setHasSearched(false);
  };

  const handlePaginationChange = (next: PaginationState) => {
    setPagination(next);
    fetchData(next, sorting);
  };

  const handleSortingChange = (next: SortingState) => {
    setSorting(next);
    // A new sort order makes the current page meaningless, so go back to one.
    const reset = { pageIndex: 0, pageSize: pagination.pageSize };
    setPagination(reset);
    fetchData(reset, next);
  };

  const handleOpenEdit = async (record: RaceLaneResult) => {
    try {
      const { original } = await getCorrectionHistory(record.raceLaneResultId);
      setOriginalRecord(original);
    } catch {
      // Still allow editing if the original cannot be fetched; the dialog just
      // renders without the before/after comparison.
      setOriginalRecord(null);
    }
    setEditRecord(record);
  };

  const handleEditSave = async (
    id: number,
    changedFields: Record<string, any>,
    reason?: string
  ) => {
    await createCorrection(id, changedFields, reason);
    fetchData(pagination, sorting);
  };

  const handleOpenHistory = useCallback(async (raceLaneResultId: number) => {
    try {
      const { corrections, original } = await getCorrectionHistory(raceLaneResultId);
      setHistoryCorrections(corrections);
      setHistoryOriginal(original);
      setHistoryRecordId(raceLaneResultId);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  }, []);

  const handleHistoryRefresh = async () => {
    if (!historyRecordId) return;
    try {
      const { corrections, original } = await getCorrectionHistory(historyRecordId);
      setHistoryCorrections(corrections);
      setHistoryOriginal(original);
      fetchData(pagination, sorting);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  /**
   * Priorities: car number, driver, class, ET, win and date identify a run and
   * stay at every width. The reaction time, quarter-mile pair and event name
   * come back at `md`; the finer splits only at `xl`, because the full set
   * needs ~1900px and a 1440 laptop should not open on a long scroll.
   */
  const columns = useMemo<ColumnDef<RaceLaneResult, any>[]>(
    () => [
      {
        id: 'hasCorrection',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.hasCorrection ? (
            <Tooltip label="This record has been corrected">
              <SquarePen aria-label="Corrected" className="size-4 text-info" />
            </Tooltip>
          ) : null,
        meta: { width: 44, incidental: true },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Tooltip label="View correction history">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="View correction history"
              onClick={(event) => {
                // The row itself opens the editor; this must not do both.
                event.stopPropagation();
                handleOpenHistory(row.original.raceLaneResultId);
              }}
            >
              <History />
            </Button>
          </Tooltip>
        ),
        meta: { width: 52, incidental: true },
      },

      { accessorKey: 'carNo', header: 'Car No', cell: ({ getValue }) => dash(getValue()), meta: { width: 90 } },
      { accessorKey: 'driver', header: 'Driver', cell: ({ getValue }) => dash(getValue()), meta: { width: 170 } },
      { accessorKey: 'carClass', header: 'Class', cell: ({ getValue }) => dash(getValue()), meta: { width: 90 } },
      // ET moved up from last position — it is the headline number of a drag
      // race and was previously the rightmost of 22 columns.
      { accessorKey: 'et', header: 'ET', cell: ({ getValue }) => dash(getValue()), meta: { width: 90, numeric: true } },
      {
        accessorKey: 'win',
        header: 'Win',
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          if (value === 'Win' || value === 'W') return <Badge variant="success">W</Badge>;
          if (value === 'Loss' || value === 'L') return <Badge variant="neutral">L</Badge>;
          return dash(value);
        },
        meta: { width: 80 },
      },
      { accessorKey: 'raceDate', header: 'Race Date', cell: ({ getValue }) => dash(getValue()), meta: { width: 120 } },

      { accessorKey: 'rt', header: 'RT', cell: ({ getValue }) => dash(getValue()), meta: { width: 90, numeric: true, priority: 'md' } },
      { accessorKey: 'ft4', header: '1/4 mi', cell: ({ getValue }) => dash(getValue()), meta: { width: 95, numeric: true, priority: 'md' } },
      { accessorKey: 'ft4Mph', header: '1/4 MPH', cell: ({ getValue }) => dash(getValue()), meta: { width: 105, numeric: true, priority: 'md' } },
      { accessorKey: 'eventName', header: 'Event', cell: ({ getValue }) => dash(getValue()), meta: { width: 170, priority: 'md' } },

      { accessorKey: 'ft60', header: '60 ft', cell: ({ getValue }) => dash(getValue()), meta: { width: 90, numeric: true, priority: 'xl' } },
      { accessorKey: 'ft330', header: '330 ft', cell: ({ getValue }) => dash(getValue()), meta: { width: 95, numeric: true, priority: 'xl' } },
      { accessorKey: 'ft8', header: '1/8 mi', cell: ({ getValue }) => dash(getValue()), meta: { width: 95, numeric: true, priority: 'xl' } },
      { accessorKey: 'ft8Mph', header: '1/8 MPH', cell: ({ getValue }) => dash(getValue()), meta: { width: 105, numeric: true, priority: 'xl' } },
      { accessorKey: 'ft1000', header: '1000 ft', cell: ({ getValue }) => dash(getValue()), meta: { width: 100, numeric: true, priority: 'xl' } },
      { accessorKey: 'dialIn', header: 'Dial-In', cell: ({ getValue }) => dash(getValue()), meta: { width: 95, numeric: true, priority: 'xl' } },
      { accessorKey: 'mov', header: 'MOV', cell: ({ getValue }) => dash(getValue()), meta: { width: 90, numeric: true, priority: 'xl' } },
      { accessorKey: 'lane', header: 'Lane', cell: ({ getValue }) => dash(getValue()), meta: { width: 80, priority: 'xl' } },
      { accessorKey: 'category', header: 'Category', cell: ({ getValue }) => dash(getValue()), meta: { width: 110, priority: 'xl' } },
      { accessorKey: 'round', header: 'Round', cell: ({ getValue }) => dash(getValue()), meta: { width: 90, priority: 'xl' } },
    ],
    [handleOpenHistory]
  );

  return (
    <div className="flex min-w-0 flex-col">
      <PageHeader
        title="Race Data Editor"
        subtitle="Results are immutable — saving records a versioned correction rather than overwriting the original."
      />

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <div className="mb-6 rounded-md border border-line bg-surface p-4 sm:p-5">
        <h2 className="mb-4 font-sans text-xs font-semibold uppercase tracking-[0.12em] text-ink-faint">
          Find results
        </h2>

        {/*
          Four columns, not seven. Seven equal columns gave each field ~136px,
          which truncated the date text to "mm/dd/yyy" and crammed the picker
          glyph against it — and every other field was cramped too. Two rows of
          four is both readable and closer to how the filters group.
        */}
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          <Field label="From Date">
            {(p) => (
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} {...p} />
            )}
          </Field>
          <Field label="To Date">
            {(p) => (
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} {...p} />
            )}
          </Field>
          <Field label="Category">
            {(p) => <Input value={category} onChange={(e) => setCategory(e.target.value)} {...p} />}
          </Field>
          <Field label="Round">
            {(p) => <Input value={round} onChange={(e) => setRound(e.target.value)} {...p} />}
          </Field>
          <Field label="Class">
            {(p) => <Input value={carClass} onChange={(e) => setCarClass(e.target.value)} {...p} />}
          </Field>
          <Field label="Driver">
            {(p) => (
              <Input value={driverName} onChange={(e) => setDriverName(e.target.value)} {...p} />
            )}
          </Field>
          <Field label="Car No">
            {(p) => <Input value={carNo} onChange={(e) => setCarNo(e.target.value)} {...p} />}
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={handleClear}>
            <X /> Clear
          </Button>
          <Button onClick={handleSearch} disabled={loading}>
            <Search /> Search
          </Button>
        </div>
      </div>

      {hasSearched && (
        <p className="mb-2 font-sans text-sm text-ink-soft">
          {total.toLocaleString()} result{total === 1 ? '' : 's'} found
        </p>
      )}

      <DataTable
        data={rows}
        columns={columns}
        getRowId={(row) => String(row.raceLaneResultId)}
        loading={loading}
        rowCount={total}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        pageSizeOptions={[25, 50, 100]}
        onRowClick={(row) => handleOpenEdit(row)}
        emptyMessage={hasSearched ? 'No results match those filters' : 'Search to load race data'}
        className="h-[calc(100dvh-26rem)] min-h-[20rem]"
      />

      <RaceDataEditDialog
        open={!!editRecord}
        record={editRecord}
        originalRecord={originalRecord}
        onClose={() => {
          setEditRecord(null);
          setOriginalRecord(null);
        }}
        onSave={handleEditSave}
      />

      {historyRecordId !== null && (
        <CorrectionHistoryDialog
          open={historyRecordId !== null}
          raceLaneResultId={historyRecordId}
          corrections={historyCorrections}
          original={historyOriginal}
          onClose={() => {
            setHistoryRecordId(null);
            setHistoryCorrections([]);
            setHistoryOriginal(null);
          }}
          onRefresh={handleHistoryRefresh}
        />
      )}
    </div>
  );
};

export default RaceDataPage;
